/**
 * Turns the chat's conversation pairs into the turns the audit log records.
 *
 * A pair holds the customer's message together with the assistant text that came
 * before it, so a turn is a pair with a customer message plus the following pair,
 * which holds the reply that message produced.
 */
import { Outcome } from "@financial-advisory/data-access/api-types";
import type { SessionGuardrailResult } from "@financial-advisory/data-access/api-types";
import { guardrailPassed } from "../guardrailUtils";
import type { ConversationPair } from "../types";

export interface AuditTurn {
  /**
   * Turn number as shown to the reader.
   */
  number: number;
  customerMessage: string;
  /**
   * The pair holding the customer's message and what the platform decided from it.
   */
  state: ConversationPair;
  /**
   * The pair holding the reply that message produced, if the agent has answered.
   */
  reply?: ConversationPair;
  /**
   * How long the turn took, in milliseconds. Only known for stored conversations.
   */
  durationMs?: number;
}

export const buildAuditTurns = (
  conversations: ConversationPair[]
): AuditTurn[] => {
  const turns: AuditTurn[] = [];

  conversations.forEach((pair, index) => {
    if (!pair.userMessage?.trim()) return;
    const reply = conversations[index + 1];
    const start = pair.userMessageAt
      ? Date.parse(pair.userMessageAt)
      : Number.NaN;
    const end = reply?.systemResponseAt
      ? Date.parse(reply.systemResponseAt)
      : Number.NaN;
    const durationMs = end - start;

    turns.push({
      number: turns.length + 1,
      customerMessage: pair.userMessage,
      state: pair,
      reply: reply?.systemResponse ? reply : undefined,
      durationMs: Number.isFinite(durationMs) && durationMs >= 0 ? durationMs : undefined,
    });
  });

  return turns;
};

/**
 * The output guardrail attempts for a turn, oldest first. Each is one draft the
 * agent wrote and what the guardrails made of it.
 */
export const draftsForTurn = (turn: AuditTurn): SessionGuardrailResult[] =>
  (turn.reply?.outputGuardrailTrees ?? [])
    .slice()
    .sort((a, b) => a.sequence_number - b.sequence_number);

export const draftFailed = (draft: SessionGuardrailResult): boolean =>
  draft.guardrail_trees?.some((tree) => !guardrailPassed(tree)) ?? false;

/**
 * How many guardrail checks ran on a turn, counting every attempt.
 */
export const checksRunOnTurn = (turn: AuditTurn): number => {
  const output = (turn.reply?.outputGuardrailTrees ?? []).reduce(
    (total, result) => total + (result.guardrail_trees?.length ?? 0),
    0
  );
  const input = (turn.state.inputGuardrailTrees ?? []).reduce(
    (total, result) => total + (result.guardrail_trees?.length ?? 0),
    0
  );
  return output + input;
};

/**
 * Outcomes this turn ruled out, compared with the turn before it.
 */
export const outcomesRuledOutOnTurn = (
  turn: AuditTurn,
  previous: AuditTurn | undefined
): number => {
  const before = new Map(
    (previous?.state.outcomeStates ?? []).map((state) => [
      state.productTreeId,
      state.outcome,
    ])
  );
  return (turn.state.outcomeStates ?? []).filter(
    (state) =>
      state.type === "suggestion" &&
      state.outcome === Outcome.FALSE &&
      before.get(state.productTreeId) !== Outcome.FALSE
  ).length;
};

/**
 * Formats a duration the way an audit reader expects to see it.
 */
export const formatDuration = (durationMs: number): string =>
  durationMs < 1000
    ? `${Math.round(durationMs)} ms`
    : `${(durationMs / 1000).toFixed(2)} s`;
